import { Trans, useLingui } from "@lingui/react/macro";
import type { StandingRule } from "@rakazo/contracts";
import { STANDING_RULE_MAX_LENGTH, STANDING_RULES_MAX } from "@rakazo/contracts";
import { Button, Input } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useId, useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";

const fieldClass =
  "h-9 rounded-lg border-transparent bg-muted/50 px-3 text-[13.5px] text-foreground shadow-none";

/**
 * A bot's standing rules: one-line "Always / Never" rules every run reads before the
 * instructions. The Space's rules, which every bot follows, show above the bot's own once there
 * are any, or to admins, who change them.
 */
export function StandingRules({ botId }: { botId: string }) {
  const { t } = useLingui();
  const id = useId();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const options = queries.standingRules.list.queryOptions({ input: { botId } });
  const { data } = useQuery(options);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!data) return null;

  async function run(action: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: queries.standingRules.key() });
      return true;
    } catch (err) {
      setError(errorText(err, t`Could not save`));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const showSpace = data.space.length > 0 || data.canEditSpace;
  if (!data.canEdit && !showSpace && data.bot.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="standing-rules">
      <span id={`${id}-label`} className="block text-xs font-medium text-muted-foreground">
        <Trans>Standing rules</Trans>
      </span>
      <RuleList
        labelledBy={`${id}-label`}
        rules={data.bot}
        editable={data.canEdit}
        busy={busy}
        onAdd={(text) => run(() => rpc.standingRules.add({ botId, text }))}
        onUpdate={(rule, text) => run(() => rpc.standingRules.update({ ruleId: rule.id, text }))}
        onRemove={(rule) => run(() => rpc.standingRules.remove({ ruleId: rule.id }))}
        testId="standing-rules-bot"
      />
      {showSpace ? (
        <>
          <span id={`${id}-space`} className="block pt-2 text-xs text-muted-foreground">
            <Trans>Every Nexa Mate in this Space</Trans>
          </span>
          <RuleList
            labelledBy={`${id}-space`}
            rules={data.space}
            editable={data.canEditSpace}
            busy={busy}
            onAdd={(text) => run(() => rpc.standingRules.add({ botId: null, text }))}
            onUpdate={(rule, text) =>
              run(() => rpc.standingRules.update({ ruleId: rule.id, text }))
            }
            onRemove={(rule) => run(() => rpc.standingRules.remove({ ruleId: rule.id }))}
            testId="standing-rules-space"
          />
        </>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RuleList({
  labelledBy,
  rules,
  editable,
  busy,
  onAdd,
  onUpdate,
  onRemove,
  testId,
}: {
  labelledBy: string;
  rules: StandingRule[];
  editable: boolean;
  busy: boolean;
  onAdd: (text: string) => Promise<boolean>;
  onUpdate: (rule: StandingRule, text: string) => Promise<boolean>;
  onRemove: (rule: StandingRule) => Promise<boolean>;
  testId: string;
}) {
  const { t } = useLingui();
  const [draft, setDraft] = useState("");
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    // Cleared at once, so Enter and the blur after it add one rule; a failure puts it back.
    setDraft("");
    void onAdd(text).then((saved) => {
      if (!saved) setDraft(text);
    });
  };
  return (
    <ul aria-labelledby={labelledBy} className="space-y-1.5" data-testid={testId}>
      {rules.map((rule) =>
        editable ? (
          <RuleRow
            key={`${rule.id}:${rule.updatedAt}`}
            rule={rule}
            busy={busy}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        ) : (
          <li key={rule.id} className="px-3 py-1.5 text-[13.5px] text-foreground" dir="auto">
            {rule.text}
          </li>
        ),
      )}
      {editable && rules.length < STANDING_RULES_MAX ? (
        <li>
          <Input
            aria-label={t`Add a rule`}
            placeholder={t`Always… or Never…`}
            className={fieldClass}
            value={draft}
            maxLength={STANDING_RULE_MAX_LENGTH}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={add}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
          />
        </li>
      ) : null}
    </ul>
  );
}

function RuleRow({
  rule,
  busy,
  onUpdate,
  onRemove,
}: {
  rule: StandingRule;
  busy: boolean;
  onUpdate: (rule: StandingRule, text: string) => Promise<boolean>;
  onRemove: (rule: StandingRule) => Promise<boolean>;
}) {
  const { t } = useLingui();
  const [draft, setDraft] = useState(rule.text);
  const commit = () => {
    const text = draft.trim();
    if (!text) return setDraft(rule.text);
    if (text !== rule.text) void onUpdate(rule, text);
  };
  return (
    <li className="flex items-center gap-1" data-testid="standing-rule">
      <Input
        aria-label={t`Rule`}
        className={fieldClass}
        value={draft}
        maxLength={STANDING_RULE_MAX_LENGTH}
        disabled={busy}
        dir="auto"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
          if (event.key === "Escape") setDraft(rule.text);
        }}
      />
      <Button
        variant="ghost"
        size="icon-sm"
        type="button"
        disabled={busy}
        aria-label={t`Remove rule`}
        onClick={() => void onRemove(rule)}
      >
        <X aria-hidden="true" />
      </Button>
    </li>
  );
}
